/**
 * Turns raw ticket events into history entries a person can read: consecutive
 * events by the same actor in the same calendar minute become one entry with a
 * bulleted list of plain-English actions.
 */

import type { SdTicketEvent } from '@/lib/serviceDeskTypes';
import { PRIORITY_LABELS, STATUS_LABELS } from './sdLabels';

export type HistoryGroup = {
  key: string;
  at: string;
  actor: string;
  lines: string[];
};

// escalation_drafted is told by its own activity line, so it is not shown twice.
const HIDDEN = new Set(['comment_added', 'note_added', 'escalation_drafted']);

function minuteOf(iso: string): number {
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const ms = Date.parse(hasZone ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(ms) ? NaN : Math.floor(ms / 60000);
}

export function actorLabel(e: SdTicketEvent): string {
  if (e.actor_name) return e.actor_name;
  if (!e.actor_email) return 'System';
  return e.actor_email.split('@')[0] ?? 'System';
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const list = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
const label = (v: unknown): string => {
  const s = str(v);
  return (STATUS_LABELS as Record<string, string>)[s] ?? (PRIORITY_LABELS as Record<string, string>)[s] ?? s;
};

function change(noun: string, from: string, to: string): string {
  if (from && to) return `Changed ${noun} from ${from} to ${to}`;
  if (to) return `Set ${noun} to ${to}`;
  return `Cleared ${noun} (was ${from})`;
}

/** One plain-English action for one event, or null when it should not be listed. */
export function eventLine(e: SdTicketEvent): string | null {
  if (HIDDEN.has(e.event_type)) return null;
  const d = e.data ?? {};
  switch (e.event_type) {
    case 'created':
      return 'Opened the ticket';
    case 'status_changed':
      return change('status', label(d['from']), label(d['to']));
    case 'reopened': {
      const from = label(d['from']);
      return from ? `Reopened the ticket (was ${from})` : 'Reopened the ticket';
    }
    case 'priority_changed':
      return change('priority', label(d['from']), label(d['to']));
    case 'assigned': {
      const to = str(d['to_name']) || str(d['to']);
      const from = str(d['from_name']) || str(d['from']);
      if (!to) return from ? `Removed the assignee (was ${from})` : 'Removed the assignee';
      return `Assigned to ${to}`;
    }
    case 'category_changed':
      return change('category', str(d['from_path']), str(d['to_path']));
    case 'topics_changed':
    case 'topic_changed': {
      const fromNames = list(d['from_names']);
      const toNames = list(d['to_names']);
      const from = fromNames.length ? fromNames : list(d['from']);
      const to = toNames.length ? toNames : list(d['to']);
      if (from.length === 0 && to.length === 0) return 'Changed topics';
      if (from.length === 0) return `Added topics: ${to.join(', ')}`;
      if (to.length === 0) return `Removed topics: ${from.join(', ')}`;
      return `Changed topics from ${from.join(', ')} to ${to.join(', ')}`;
    }
    case 'title_changed':
      return change('title', str(d['from']), str(d['to']));
    case 'vendor_ticket_set':
      return change('vendor ticket number', str(d['from']), str(d['to']));
    case 'hr_restricted_changed':
      return d['to'] === true ? 'Restricted the ticket to HR' : 'Removed the HR restriction';
    case 'mention': {
      const who = str(d['name']) || str(d['email']);
      return who ? `Mentioned ${who}` : 'Mentioned someone';
    }
    case 'attachment_added':
      return str(d['filename']) ? `Added attachment ${str(d['filename'])}` : 'Added an attachment';
    case 'moved_desk':
      return str(d['to_key']) ? `Moved to ${str(d['to_key'])}` : 'Moved the ticket to another desk';
    case 'confirmed_bug':
      return 'Confirmed this is a bug';
    case 'hr_approval_requested': {
      const n = Array.isArray(d['approvers']) ? d['approvers'].length : 0;
      return n > 0 ? `Asked ${n} HR approvers to approve this` : 'Asked HR to approve this';
    }
    case 'hr_approved':
      return 'HR approved this';
    case 'hr_declined':
      return str(d['reason']) ? `HR declined this: ${str(d['reason'])}` : 'HR declined this';
    case 'hr_approval_reminded':
      return 'Reminded the HR approvers';
    case 'offboarding_ready':
      return 'Marked ready to offboard';
    case 'sla_warning':
      return 'SLA warning sent';
    case 'sla_breached':
      return 'SLA deadline passed';
    case 'activity':
      return str(d['text']) || 'Logged an interaction';
    case 'details_completed':
      return str(d['category_path']) ? `Finished the request: ${str(d['category_path'])}` : 'Finished the request';
    case 'vendor_emailed':
      if (d['not_sent']) return `Email to RapidPOS not sent (${str(d['not_sent']).replace(/^not sent: /, '')})`;
      return str(d['to']) ? `Emailed RapidPOS at ${str(d['to'])}` : 'Emailed RapidPOS';
    case 'automation_run': {
      const rule = str(d['rule']);
      return rule ? `Ran the automation ${rule}` : 'Ran an automation';
    }
    default:
      return e.event_type.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase());
  }
}

/** Groups events (any order) into history entries, oldest first. */
export function groupEvents(events: SdTicketEvent[]): HistoryGroup[] {
  const sorted = [...events].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id);
  const groups: HistoryGroup[] = [];
  let lastMinute = NaN;
  for (const e of sorted) {
    const line = eventLine(e);
    if (line === null) continue;
    const actor = actorLabel(e);
    const minute = minuteOf(e.created_at);
    const last = groups[groups.length - 1];
    if (last && last.actor === actor && minute === lastMinute) {
      last.lines.push(line);
    } else {
      groups.push({ key: `g${e.id}`, at: e.created_at, actor, lines: [line] });
    }
    lastMinute = minute;
  }
  return groups;
}
